"use client"

import * as React from "react"
import { Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { formatPrice, toFaDigits } from "@/lib/format"
import { usdToToman } from "@/lib/fx"
import { cn } from "@/lib/utils"
import { orderAiApiModels, type AiApiModelKind, type AiApiModelPrice } from "@/content/ai-api-models"

const KIND_LABEL: Record<AiApiModelKind, string> = {
  text: "متن",
  image: "تصویر",
  video: "ویدیو",
  audio: "صوت",
}

type KindFilter = "all" | "text" | "image" | "video"

const KIND_FILTERS: { id: KindFilter; label: string }[] = [
  { id: "all", label: "همه" },
  { id: "text", label: "متن" },
  { id: "image", label: "تصویر" },
  { id: "video", label: "ویدیو" },
]

const INITIAL_VISIBLE = 12
const PAGE_SIZE = 12

function toman(usd: number | null, rate: number) {
  return usd == null ? null : usdToToman(usd, rate)
}

function modelPriceText(model: AiApiModelPrice, rate: number) {
  const parts: string[] = []
  const input = toman(model.inputUsdPerMillion, rate)
  const output = toman(model.outputUsdPerMillion, rate)
  const perImage = toman(model.outputUsdPerImage, rate)
  const perSecond = toman(model.outputUsdPerSecond, rate)
  if (input != null) parts.push(`ورودی ${formatPrice(input)}`)
  if (output != null) parts.push(`خروجی ${formatPrice(output)}`)
  if (perImage != null) parts.push(`${formatPrice(perImage)} / تصویر`)
  if (perSecond != null) parts.push(`${formatPrice(perSecond)} / ثانیه`)
  return parts.length ? parts.join(" · ") : "—"
}

function ModelRow({ model }: { model: AiApiModelPrice }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[0.9375rem] font-extrabold text-[var(--content)]">{model.name}</span>
        <Badge variant="neutral" size="sm">
          {KIND_LABEL[model.kind]}
        </Badge>
      </div>
      <p className="mt-1 ltr-nums text-[0.75rem] font-normal text-[var(--content-subtle)]" dir="ltr">
        {model.id}
      </p>
    </>
  )
}

export function AiApiPriceTable({
  models,
  tomanPerUsd,
  className,
}: {
  models: AiApiModelPrice[]
  tomanPerUsd: number
  className?: string
}) {
  const [query, setQuery] = React.useState("")
  const [kind, setKind] = React.useState<KindFilter>("all")
  const [visible, setVisible] = React.useState(INITIAL_VISIBLE)
  const inputId = React.useId()
  const q = query.trim().toLowerCase()
  const ordered = React.useMemo(() => orderAiApiModels(models), [models])
  const filtered = ordered.filter((m) => {
    if (kind !== "all" && m.kind !== kind) return false
    if (!q) return true
    return (
      m.name.toLowerCase().includes(q) ||
      m.id.toLowerCase().includes(q) ||
      m.provider.toLowerCase().includes(q) ||
      KIND_LABEL[m.kind].includes(query.trim())
    )
  })

  React.useEffect(() => {
    setVisible(INITIAL_VISIBLE)
  }, [q, kind])

  const searching = Boolean(q) || kind !== "all"
  const shown = searching ? filtered : filtered.slice(0, visible)
  const hiddenCount = searching ? 0 : Math.max(0, filtered.length - visible)

  return (
    <div className={cn("min-w-0", className)}>
      <div className="relative">
        <label htmlFor={inputId} className="sr-only">
          جستجوی مدل
        </label>
        <Search
          className="pointer-events-none absolute inset-y-0 start-3.5 my-auto size-4 text-[var(--content-subtle)]"
          aria-hidden
        />
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="جستجوی مدل"
          className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--hairline-strong)] bg-[var(--surface)] pe-3 ps-10 text-[0.875rem] text-[var(--content)] outline-none placeholder:text-[var(--content-subtle)] focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="فیلتر نوع مدل">
        {KIND_FILTERS.map((item) => {
          const active = kind === item.id
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setKind(item.id)}
              aria-pressed={active}
              className={cn(
                "rounded-full px-4 py-2 text-[0.8125rem] font-semibold",
                "transition-all duration-200 ease-[var(--ease-out-quint)]",
                active
                  ? "bg-brand-600 text-white shadow-[0_8px_20px_-10px_rgb(34_113_198/0.8)]"
                  : "bg-[var(--canvas-subtle)] text-[var(--content-muted)] ring-1 ring-[var(--hairline)] hover:text-[var(--content)]",
              )}
            >
              {item.label}
            </button>
          )
        })}
      </div>

      <p className="mt-3 text-[0.8125rem] text-[var(--content-muted)]">
        {filtered.length === models.length
          ? `${toFaDigits(models.length)} مدل`
          : `${toFaDigits(filtered.length)} از ${toFaDigits(models.length)} مدل`}
        . قیمت متن و تصویر برای هر میلیون توکن است. قیمت ویدیو برای هر ثانیه خروجی است چون مدل ویدیو مدت کلیپ را می‌فروشد، نه توکن.
      </p>

      <ul className="mt-4 grid gap-3 md:hidden">
        {shown.map((model) => (
          <li
            key={model.id}
            className="rounded-[var(--radius-lg)] border border-[var(--hairline-strong)] bg-[var(--surface)] p-4 shadow-sm"
          >
            <ModelRow model={model} />
            <p className="mt-3 text-[0.8125rem] font-bold text-[var(--content)]">{modelPriceText(model, tomanPerUsd)}</p>
          </li>
        ))}
      </ul>

      <div className="mt-4 hidden max-w-full min-w-0 overflow-x-auto overscroll-x-contain rounded-[var(--radius-xl)] border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-sm md:block">
        <table className="w-full min-w-[36rem] border-collapse">
          <caption className="sr-only">تعرفه مدل‌های API هوش مصنوعی</caption>
          <thead>
            <tr className="border-b border-[var(--hairline)]">
              <th scope="col" className="p-4 text-start text-[0.75rem] font-bold text-[var(--content-subtle)]">
                مدل
              </th>
              <th scope="col" className="p-4 text-start text-[0.75rem] font-bold text-[var(--content-subtle)]">
                قیمت
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((model, index) => (
              <tr key={model.id} className={cn(index % 2 === 1 && "bg-[var(--canvas-subtle)]")}>
                <th scope="row" className="p-4 text-start align-top">
                  <ModelRow model={model} />
                </th>
                <td className="p-4 align-top text-[0.8125rem] font-bold text-[var(--content)]">
                  {modelPriceText(model, tomanPerUsd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hiddenCount > 0 ? (
        <div className="mt-6 flex justify-center">
          <Button type="button" variant="secondary" onClick={() => setVisible((n) => n + PAGE_SIZE)}>
            بیشتر
            <span className="font-normal text-[var(--content-muted)]">
              {toFaDigits(hiddenCount)} مدل دیگر
            </span>
          </Button>
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <p className="mt-6 text-[0.875rem] text-[var(--content-muted)]">مدلی با این نام پیدا نشد.</p>
      ) : null}
    </div>
  )
}
